/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Tooltip, LegacyDataTable, LegacyDataTableBody, LegacyDataTableRow, LegacyDataTableRowColumn, LegacyDataTableRowHeaderColumn } from "@octopusdeploy/design-system-components";
import { ArrowDownToSquareIcon } from "@octopusdeploy/design-system-icons";
import type { PackageFromBuiltInFeedResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { repository } from "~/clientInstance";
import BuildInformationTable from "~/components/BuildInformation/BuildInformationTable";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import Markdown from "~/components/Markdown";
import InternalRedirect from "~/components/Navigation/InternalRedirect";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { Section } from "~/components/Section/Section";
import { FormSectionHeading } from "~/components/form";
import ByteSizeFormatter from "~/utils/ByteSizeFormatter";
import DateFormatter from "~/utils/DateFormatter";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import { client } from "../../../../clientInstance";
interface PackageDetailsPageProps {
    spaceId: string;
    packageId: string;
}
interface PackageDetailsPageState extends DataBaseComponentState {
    package?: PackageFromBuiltInFeedResource;
    deleted?: boolean;
}
export class PackageDetailsPage extends DataBaseComponent<PackageDetailsPageProps, PackageDetailsPageState> {
    constructor(props: PackageDetailsPageProps) {
        super(props);
        this.state = {};
    }
    async componentDidMount() {
        await this.doBusyTask(() => this.load(), { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    async load() {
        const pkg = await repository.Packages.get(this.props.packageId, { includeNotes: true });
        let errors = null;
        if (pkg && pkg.PackageVersionBuildInformation && pkg.PackageVersionBuildInformation.IncompleteDataWarning) {
            errors = { message: `Unable to retrieve part of the package build information for package '${pkg.PackageId}'.`, errors: { PackageVersionBuildInformationIncomplete: pkg.PackageVersionBuildInformation.IncompleteDataWarning } };
        }
        this.setState({ package: pkg });
        if (!errors) {
            this.clearErrors();
        }
        else {
            this.setValidationErrors(errors.message, errors.errors);
        }
    }
    render() {
        const pkg = this.state.package;
        const title = this.state.package && pkg!.PackageId + " " + pkg!.Version;
        const uploadPackagePageAction: PageAction = {
            type: "navigate",
            hasPermissions: isAllowed({ permission: Permission.BuiltInFeedDownload, project: "*" }),
            buttonType: "secondary",
            label: "Upload another package",
            path: links.builtInPackageRepositoryPage.generateUrl({ spaceId: this.props.spaceId }),
        };
        const pageActions: PageAction[] = pkg ? [uploadPackagePageAction] : [];
        const downloadPackagePageAction: PrimaryPageAction | undefined = pkg
            ? {
                type: "navigate",
                hasPermissions: isAllowed({ permission: Permission.BuiltInFeedDownload, project: "*" }),
                label: "Download",
                path: client.resolve(pkg.Links["Raw"]),
                icon: <ArrowDownToSquareIcon size={24}/>,
                external: true,
                isDownload: true,
            }
            : undefined;
        const overflowActions = OverflowMenuConverterVNext.convertAll([
            OverflowMenuItems.deleteItemDefault(title!, this.handleDeleteConfirm, { permission: Permission.BuiltInFeedAdminister, project: "*" }, "Deleting packages will permanently remove files from disk"),
        ]);
        return (<PageContent header={{
                title: title ?? "",
                breadcrumbs: pkg ? [{ label: pkg.PackageId, pageUrl: links.packageVersionsPage.generateUrl({ spaceId: this.props.spaceId, packageId: pkg.PackageId }) }] : undefined,
                primaryAction: downloadPackagePageAction,
                pageActions,
                overflowActions: overflowActions.menuItems,
            }} busy={this.state.busy} errors={this.errors}>
                {this.state.deleted && <InternalRedirect to={links.builtInPackageRepositoryPage.generateUrl({ spaceId: this.props.spaceId })}/>}
                {pkg && overflowActions.dialogs}
                {pkg && (<Section>
                        <p>
                            The information below is from the specification embedded in the <strong>{`${pkg.PackageId}.${pkg.Version}${pkg.FileExtension}`}</strong> file.
                        </p>
                    </Section>)}
                {pkg && (<LegacyDataTable>
                        <LegacyDataTableBody>
                            <LegacyDataTableRow>
                                <LegacyDataTableRowHeaderColumn>ID</LegacyDataTableRowHeaderColumn>
                                <LegacyDataTableRowColumn>{pkg.PackageId}</LegacyDataTableRowColumn>
                            </LegacyDataTableRow>
                            <LegacyDataTableRow>
                                <LegacyDataTableRowHeaderColumn>Version</LegacyDataTableRowHeaderColumn>
                                <LegacyDataTableRowColumn>{pkg.Version}</LegacyDataTableRowColumn>
                            </LegacyDataTableRow>
                            <LegacyDataTableRow>
                                <LegacyDataTableRowHeaderColumn>Published</LegacyDataTableRowHeaderColumn>
                                <LegacyDataTableRowColumn>
                                    <Tooltip content={DateFormatter.dateToLongFormat(pkg.Published)!}>{DateFormatter.dateToShortFormat(pkg.Published)}</Tooltip>
                                </LegacyDataTableRowColumn>
                            </LegacyDataTableRow>
                            {pkg.Title && (<LegacyDataTableRow>
                                    <LegacyDataTableRowHeaderColumn>Title</LegacyDataTableRowHeaderColumn>
                                    <LegacyDataTableRowColumn>{pkg.Title}</LegacyDataTableRowColumn>
                                </LegacyDataTableRow>)}
                            {pkg.Summary && (<LegacyDataTableRow>
                                    <LegacyDataTableRowHeaderColumn>Summary</LegacyDataTableRowHeaderColumn>
                                    <LegacyDataTableRowColumn>{pkg.Summary}</LegacyDataTableRowColumn>
                                </LegacyDataTableRow>)}
                            {pkg.Description && (<LegacyDataTableRow>
                                    <LegacyDataTableRowHeaderColumn>Description</LegacyDataTableRowHeaderColumn>
                                    <LegacyDataTableRowColumn>{pkg.Description}</LegacyDataTableRowColumn>
                                </LegacyDataTableRow>)}
                            {pkg.ReleaseNotes && (<LegacyDataTableRow>
                                    <LegacyDataTableRowHeaderColumn>Release Notes</LegacyDataTableRowHeaderColumn>
                                    <LegacyDataTableRowColumn>
                                        <Markdown markup={pkg.ReleaseNotes}/>
                                    </LegacyDataTableRowColumn>
                                </LegacyDataTableRow>)}
                            {pkg.FileExtension && (<LegacyDataTableRow>
                                    <LegacyDataTableRowHeaderColumn>File Extension</LegacyDataTableRowHeaderColumn>
                                    <LegacyDataTableRowColumn>{pkg.FileExtension}</LegacyDataTableRowColumn>
                                </LegacyDataTableRow>)}
                            <LegacyDataTableRow>
                                <LegacyDataTableRowHeaderColumn>Size</LegacyDataTableRowHeaderColumn>
                                <LegacyDataTableRowColumn>{ByteSizeFormatter(pkg.PackageSizeBytes!)}</LegacyDataTableRowColumn>
                            </LegacyDataTableRow>
                            <LegacyDataTableRow>
                                <LegacyDataTableRowHeaderColumn>SHA1</LegacyDataTableRowHeaderColumn>
                                <LegacyDataTableRowColumn>{pkg.Hash}</LegacyDataTableRowColumn>
                            </LegacyDataTableRow>
                        </LegacyDataTableBody>
                    </LegacyDataTable>)}
                {pkg && pkg.PackageVersionBuildInformation && (<>
                        <FormSectionHeading title="Build Information"/>
                        <BuildInformationTable data={pkg.PackageVersionBuildInformation}/>
                    </>)}
            </PageContent>);
    }
    private handleDeleteConfirm = async () => {
        await repository.Packages.deleteMany([this.props.packageId]);
        this.setState({ deleted: true });
        return true;
    };
    static displayName = "PackageDetailsPage";
}
